import { css } from '@emotion/react'
import { Button, Cell, Flow, Grid, HFlow, Icon, VFlow } from 'bold-ui'
import {
  CepField,
  Form,
  FormRenderProps,
  RadioGroupField,
  resetAllFieldStates,
  SubmitButton,
  TextField,
  TipoLogradouroSelectField,
  UnidadeSaudeSelectField,
} from 'components/form'
import { AldeiaSelectField } from 'components/form/field/select/AldeiaSelectField'
import { DseiSelectField } from 'components/form/field/select/DseiSelectField'
import { EquipeSelectField } from 'components/form/field/select/EquipeSelectField/EquipeSelectField'
import { MicroareaSelectField } from 'components/form/field/select/MicroareaSelectField/MicroareaSelectField'
import { PoloBaseSelectField } from 'components/form/field/select/PoloBaseSelectField'
import { TipoEnderecoEnum } from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { metaPath } from 'util/metaPath'
import { tipoEnderecoIndigena, tipoEnderecoOptions } from 'view/cadastro-imovel/model-cadastroImovel'
import { useSaudeIndigenaAuthorization } from 'view/cidadao/authorization/useSaudeIndigenaAuthorization'

import { createVisualizacaoTerritorialCalculator } from './calculator-visualizacaoTerritorial'
import { TerritorioViewTabEnum } from './components/list/TerritorioContentSwitch'
import { VisualizacaoTerritorialBuscaFormModel } from './model'
import { validateVisualizacaoTerritorialForm } from './validator'

const meta = metaPath<VisualizacaoTerritorialBuscaFormModel>()

interface VisualizacaoTerritorialFormProps {
  tabContext: TerritorioViewTabEnum
  onSubmit(formValues: VisualizacaoTerritorialBuscaFormModel): void
  appliedFilter: VisualizacaoTerritorialBuscaFormModel
  defaultFilter: VisualizacaoTerritorialBuscaFormModel
}

export function VisualizacaoTerritorialBuscaForm(props: Readonly<VisualizacaoTerritorialFormProps>) {
  const { onSubmit, tabContext, appliedFilter, defaultFilter } = props

  const { hasTerritorioIndigenaPermission } = useSaudeIndigenaAuthorization()
  const decorators = useMemo(() => [createVisualizacaoTerritorialCalculator(meta)], [])

  const isTabListaDeImoveis = tabContext === TerritorioViewTabEnum.LISTA_DE_IMOVEIS
  const isTabMapaDoTerritorio = tabContext === TerritorioViewTabEnum.MAPA_DO_TERRITORIO

  const renderForm = ({ handleSubmit, form, values }: FormRenderProps<VisualizacaoTerritorialBuscaFormModel>) => {
    const isEnderecoLogradouro = !tipoEnderecoIndigena.includes(values?.tipoEndereco as TipoEnderecoEnum)
    const isEnderecoIndigena = tipoEnderecoIndigena.includes(values?.tipoEndereco as TipoEnderecoEnum)
    const isBuscarDisabled = isEnderecoIndigena && (!values?.poloBase || !values?.dsei)

    const resetToInitialValues = () => {
      resetAllFieldStates(form)
      form.reset({
        unidadeSaude: defaultFilter.unidadeSaude,
        equipe: defaultFilter.equipe,
        tipoEndereco: values.tipoEndereco,
      })
    }

    return (
      <VFlow vSpacing={4}>
        <Grid wrap>
          <Cell size={isTabListaDeImoveis ? 4 : 12}>
            <UnidadeSaudeSelectField name={meta.unidadeSaude} label='Unidade de saúde' required mostrarInativas />
          </Cell>
          <Cell size={isTabListaDeImoveis ? 4 : 12}>
            <EquipeSelectField
              name={meta.equipe}
              label='Equipe'
              unidadeSaudeId={values?.unidadeSaude?.id}
              disabled={!values?.unidadeSaude?.id}
              required
              showSemEquipeOption
              mostrarInativas
            />
          </Cell>
          {isTabMapaDoTerritorio && (
            <Cell size={12}>
              <MicroareaSelectField
                name={meta.microareas}
                label='Microárea'
                metaEquipe={meta.equipe}
                placeholder='Todas as microáreas'
              />
            </Cell>
          )}
          {isTabListaDeImoveis && <Cell size={4} />}
          {hasTerritorioIndigenaPermission && isTabListaDeImoveis && (
            <Cell size={12}>
              <RadioGroupField
                label='Tipo de endereço'
                name={meta.tipoEndereco}
                options={tipoEnderecoOptions}
                required
              />
            </Cell>
          )}
          {isEnderecoLogradouro && isTabListaDeImoveis && (
            <Fragment>
              <Cell size={4}>
                <TextField name={meta.bairro} label='Bairro' maxLength={72} />
              </Cell>
              <Cell size={2}>
                <CepField name={meta.cep} label='CEP' />
              </Cell>
              <Cell size={6} />
              <Cell size={3}>
                <TipoLogradouroSelectField name={meta.tipoLogradouro} label='Tipo de logradouro' />
              </Cell>
              <Cell size={5}>
                <TextField name={meta.logradouro} label='Logradouro' maxLength={72} />
              </Cell>
            </Fragment>
          )}
          {isEnderecoIndigena && isTabListaDeImoveis && (
            <Fragment>
              <Cell size={4}>
                <DseiSelectField
                  name={meta.dsei}
                  label='DSEI'
                  poloBaseId={values.poloBase?.id}
                  aldeiaId={values.aldeia?.id}
                  required
                />
              </Cell>
              <Cell size={4}>
                <PoloBaseSelectField
                  name={meta.poloBase}
                  label='Polo base'
                  dseiId={values.dsei?.id}
                  aldeiaId={values.aldeia?.id}
                  required
                />
              </Cell>
              <Cell size={4}>
                <AldeiaSelectField
                  name={meta.aldeia}
                  label='Aldeia'
                  dseiId={values.dsei?.id}
                  poloBaseId={values.poloBase?.id}
                />
              </Cell>
            </Fragment>
          )}
          {isTabListaDeImoveis && (
            <Cell size={isEnderecoIndigena ? 12 : 3}>
              <HFlow hSpacing={0.5} justifyContent={isEnderecoIndigena ? 'flex-end' : 'flex-start'}>
                <SubmitButton
                  size='small'
                  handleSubmit={handleSubmit}
                  style={css`
                    margin-top: 1.45rem;
                  `}
                  disabled={isBuscarDisabled}
                >
                  <Icon
                    icon='zoomOutline'
                    style={css`
                      margin-right: 0.5rem;
                    `}
                  />
                  Buscar
                </SubmitButton>
                <Button
                  onClick={resetToInitialValues}
                  size='small'
                  style={css`
                    margin-top: 1.45rem;
                  `}
                >
                  Limpar busca
                </Button>
              </HFlow>
            </Cell>
          )}
          {isTabMapaDoTerritorio && (
            <Cell size={12}>
              <Flow direction='horizontal' justifyContent='flex-end' gap={0.5}>
                <Button onClick={resetToInitialValues} size='small'>
                  Limpar campos
                </Button>
                <SubmitButton size='small' handleSubmit={handleSubmit} disabled={isBuscarDisabled}>
                  <Icon
                    icon='zoomOutline'
                    style={css`
                      margin-right: 0.5rem;
                    `}
                  />
                  Buscar
                </SubmitButton>
              </Flow>
            </Cell>
          )}
        </Grid>
      </VFlow>
    )
  }

  return (
    <Form<VisualizacaoTerritorialBuscaFormModel>
      render={renderForm}
      onSubmit={onSubmit}
      validate={validateVisualizacaoTerritorialForm(tabContext)}
      initialValues={appliedFilter}
      decorators={decorators}
    />
  )
}
